import {
	type HttpErrorResponse,
	type HttpEvent,
	type HttpHandler,
	type HttpInterceptor,
	type HttpRequest,
	HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { type Observable, throwError } from 'rxjs';

import { NotificationService } from '../services/notification.service';
import { AppConfigService } from '../services/app-config.service';
import { catchHttpErrorResponse } from '../utils/catch-http-error-response';
import { isValidationErrorDto } from '../services/mappers/dto/validation-error.dto';

/**
 * Intercepts HTTP errors and displays global user notifications for unhandled errors.
 * Shows error messages to users when errors are not caught by components or services.
 */
@Injectable()
export class ErrorNotificationInterceptor implements HttpInterceptor {
	private readonly appConfigService = inject(AppConfigService);

	private readonly snackBarService = inject(NotificationService);

	/**
	 * Intercepts HTTP requests and handles validation errors.
	 * @inheritdoc
	 */
	public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!this.shouldIntercept(req.url)) {
			return next.handle(req);
		}

		return next.handle(req)
			.pipe(
				catchHttpErrorResponse((httpError: HttpErrorResponse) => {
					if (httpError.status === HttpStatusCode.BadRequest) {
						this.showGlobalValidationError(httpError);
					}
					return throwError(() => httpError);
				}),
			);
	}

	/**
	 * Shows global validation error notification to the user.
	 * Displays validation errors when backend returns general errors without field-specific details.
	 * @param httpError Bad request HTTP error response.
	 */
	private showGlobalValidationError(httpError: HttpErrorResponse): void {
		// Show global validation errors when backend doesn't specify which field the error occurs from.
		if (isValidationErrorDto(httpError.error)) {
			const { errors } = httpError.error;

			if (errors.length === 0) {
				return;
			}

			this.snackBarService.showErrorNotification({
				label: 'Errors occurred:',
				items: errors.map(error => ({
					message: error.detail,
					title: error.attr === 'non_field_errors' ? '' : error.attr,
				})).filter(item => item.message !== ''),
			});
		}
	}

	/**
	 * Checks if a request should be intercepted.
	 * @param url Request url.
	 */
	private shouldIntercept(url: string): boolean {
		return url.startsWith(this.appConfigService.apiUrl);
	}
}
